/**
 * Besoins: faim et energie, de 0 a 100.
 *
 * Regles du proprietaire du jeu: on ne meurt JAMAIS et on ne perd JAMAIS
 * d'objet. Tomber a zero ne fait que ralentir (facteur RALENTI_MIN), jamais
 * plus. La descente est douce: une journee complete de 24 minutes coute
 * FAIM_PAR_JOUR (35) et ENERGIE_PAR_JOUR (60) points, donc on tient pres de
 * trois journees sans rien manger.
 */
import {
  DAY_LENGTH_MS,
  ENERGIE_PAR_JOUR,
  ENERGIE_PAR_RECOLTE,
  FAIM_PAR_JOUR,
  RALENTI_MIN,
  SEUIL_BAS,
} from '../constants.ts'
import type { Besoins } from '../types/player.ts'
import type { ItemId } from '../types/items.ts'
import { clamp, lerp } from './alea.ts'
import { OBJETS } from '../data/objets.ts'

/** Faim perdue par une nuit de sommeil complete. */
export const FAIM_PAR_NUIT = 6

/** Plancher du facteur de recolte: recolter a plat coute plus cher que marcher. */
export const RALENTI_RECOLTE_MIN = 0.45

export function besoinsNeufs(): Besoins {
  return { faim: 100, energie: 100 }
}

/**
 * Fait descendre les besoins. `travail` de 0 a 1: a 1 l'energie descend deux
 * fois plus vite (recolte, construction).
 */
export function majBesoins(b: Besoins, dtMsJeu: number, travail: number): Besoins {
  if (dtMsJeu <= 0) return { faim: clamp(b.faim, 0, 100), energie: clamp(b.energie, 0, 100) }
  const jours = dtMsJeu / DAY_LENGTH_MS
  const t = clamp(travail, 0, 1)
  return {
    faim: clamp(b.faim - FAIM_PAR_JOUR * jours, 0, 100),
    energie: clamp(b.energie - ENERGIE_PAR_JOUR * jours * (1 + t), 0, 100),
  }
}

/**
 * Energie rendue par une journee de jeu passee dans le spa, en points.
 *
 * Une journee dure 24 minutes reelles: 900 points par jour, c'est un peu plus
 * de trente points par minute reelle, une fois la descente ordinaire deduite.
 * Assez pour qu'un bain vaille le detour, trop peu pour remplacer une nuit —
 * dormir rend toute l'energie d'un coup, le spa la rend pendant qu'on y est.
 *
 * Le spa ne touche PAS a la faim: on ne mange pas dans son bain.
 */
export const ENERGIE_SPA_PAR_JOUR = 900

/**
 * Un temps passe dans le spa. A appeler APRES `majBesoins` du meme pas: la
 * descente ordinaire s'applique toujours, le spa la compense et la depasse.
 */
export function reposerAuSpa(b: Besoins, dtMsJeu: number): Besoins {
  if (dtMsJeu <= 0) return b
  const jours = dtMsJeu / DAY_LENGTH_MS
  return { faim: b.faim, energie: clamp(b.energie + ENERGIE_SPA_PAR_JOUR * jours, 0, 100) }
}

/** Rampe commune: 1 au-dessus du seuil, `plancher` a zero. */
function rampe(v: number, plancher: number): number {
  if (v >= SEUIL_BAS) return 1
  return lerp(plancher, 1, clamp(v, 0, SEUIL_BAS) / SEUIL_BAS)
}

/**
 * LA FORME du bonhomme, de 0 (a plat) a 1 (repose et rassasie).
 *
 * C'est la seule chose que le serveur sache des besoins de qui que ce soit: le
 * client la met dans le champ `f` de chaque entree (shared/sim/entree.ts) parce
 * que le serveur, lui, ne tient ni la faim ni l'energie.
 *
 * Elle porte toute l'echelle, et pas seulement la penalite, parce que DEUX
 * regles en descendent maintenant: `facteurVitesseForme`, qui ralentit celui
 * qui a faim, et `elanForme`, qui donne son allure au nageur en pleine forme.
 * Un facteur borne a 1 des SEUIL_BAS ne saurait pas distinguer une energie de
 * 30 d'une energie de 100.
 */
export function formeDe(b: Besoins): number {
  return clamp(Math.min(b.faim, b.energie), 0, 100) / 100
}

/**
 * Le ralentissement de la faim et de la fatigue, calcule a partir de la forme
 * transmise. C'est `facteurVitesse` vue depuis le reseau: meme rampe, memes
 * nombres, une seule regle.
 */
export function facteurVitesseForme(forme: number): number {
  return rampe(clamp(forme, 0, 1) * 100, RALENTI_MIN)
}

/**
 * De 0 a 1: ce qui reste de forme AU-DESSUS du seuil de ralentissement.
 *
 * Nul tant qu'on traine sous SEUIL_BAS — on y est deja ralenti par
 * `facteurVitesseForme`, et retirer en plus un elan qu'on n'a pas encore serait
 * punir deux fois la meme fatigue. Au-dessus, ca monte tout droit jusqu'a 1.
 */
export function elanForme(forme: number): number {
  const v = clamp(forme, 0, 1) * 100
  if (v <= SEUIL_BAS) return 0
  return (v - SEUIL_BAS) / (100 - SEUIL_BAS)
}

/** Multiplicateur de vitesse de deplacement, de RALENTI_MIN a 1. */
export function facteurVitesse(b: Besoins): number {
  return facteurVitesseForme(formeDe(b))
}

/** Multiplicateur de vitesse de recolte, de RALENTI_RECOLTE_MIN a 1. */
export function facteurRecolte(b: Besoins): number {
  // L'energie compte double: c'est elle qui sert a frapper.
  const v = Math.min(b.energie, (b.energie + b.faim) / 2)
  return rampe(v, RALENTI_RECOLTE_MIN)
}

/**
 * Cout fixe d'un coup de recolte, en plus de la descente continue.
 * A appeler une fois par coup porte (voir frapper dans ./ressources.ts).
 */
export function depenserRecolte(b: Besoins, coups = 1): Besoins {
  return {
    faim: b.faim,
    energie: clamp(b.energie - ENERGIE_PAR_RECOLTE * coups, 0, 100),
  }
}

export function manger(b: Besoins, id: ItemId): Besoins {
  const def = OBJETS[id]
  return {
    faim: clamp(b.faim + def.faim, 0, 100),
    energie: clamp(b.energie + def.energie, 0, 100),
  }
}

/** Une nuit de sommeil: energie pleine, mais ca creuse l'appetit. */
export function dormir(b: Besoins): Besoins {
  return {
    faim: clamp(b.faim - FAIM_PAR_NUIT, 0, 100),
    energie: 100,
  }
}

/** Etat d'une jauge, pour sa couleur dans l'interface. */
export function etatBesoin(v: number): 'bon' | 'moyen' | 'bas' | 'critique' {
  if (v >= 60) return 'bon'
  if (v >= SEUIL_BAS) return 'moyen'
  if (v >= 10) return 'bas'
  return 'critique'
}
